import {PageHeader} from "../menu/PageHeader";
import {Badge, Button, Card, Col, Container, Row} from "react-bootstrap";
import React, {useContext, useEffect, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {UsacmContext} from "../App";
import {getCourseOrder} from "../api/CourseApi";
import {getErrorMessageForField} from "../utils/formUtils";
import {
  CONF_LABEL_KEY_COURSE_PURCHASED_CONTENT,
  COURSE_ORDER_STATUS_APPROVED,
  COURSE_ORDER_STATUS_DECLINED,
  COURSE_ORDER_STATUS_IN_PROGRESS,
  COURSE_ORDER_STATUS_NAMES,
  COURSE_ORDER_STATUS_PENDING,
  COURSE_ORDER_STATUS_PENDING_REVIEW,
  COURSE_REVIEW_STATUS_NAMES,
} from "../constants";
import {getConfLabel} from "../utils/usacmUtils";
import {getCoursePriceAccessStr, getCoursePriceAmountStr, loadCourseCartCount} from "./courseUtils";

// The Stripe payment intent status when the payment did not go through (it can be tried again from the cart)
const STRIPE_STATUS_REQUIRES_PAYMENT_METHOD = 'requires_payment_method';

/**
 * One of the user's course orders - /course-order?id=N.  Stripe sends the user here after they pay (adding
 * payment_intent and redirect_status to the url).  Loading the order asks Stripe how the payment went, so the order
 * is completed here even if the webhook has not arrived yet.
 */
export function CourseOrderView() {
  const context = useContext(UsacmContext);
  const [conf,] = context.conference;
  const [navChanged,] = context.navigationChanged;
  const [, setCourseCartCount] = context.courseCartCount;
  const navigate = useNavigate();
  const {search} = useLocation();
  const searchParams = new URLSearchParams(search);
  const courseOrderId = searchParams.get('id');
  const redirectStatus = searchParams.get('redirect_status'); // set by Stripe
  const purchasedHtml = getConfLabel(conf, CONF_LABEL_KEY_COURSE_PURCHASED_CONTENT);
  const [order, setOrder] = useState(null);
  const [errors, setErrors] = useState([]);

  function loadOrder() {
    getCourseOrder(courseOrderId, (status, data, newErrors) => {
      if (status === 200) {
        setOrder(data);
        setErrors([]);
        // Checking out empties the cart
        loadCourseCartCount(setCourseCartCount);
      } else {
        setErrors(newErrors);
      }
    });
  }

  useEffect(() => {
    if (courseOrderId) {
      loadOrder();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navChanged, courseOrderId]);

  const orderStatus = order?.order_status;
  const paymentFailed = orderStatus === COURSE_ORDER_STATUS_IN_PROGRESS &&
    (redirectStatus === 'failed' || order?.payment_status === STRIPE_STATUS_REQUIRES_PAYMENT_METHOD);
  const waitingForStripe = orderStatus === COURSE_ORDER_STATUS_IN_PROGRESS && !paymentFailed;
  const completed = orderStatus === COURSE_ORDER_STATUS_APPROVED || orderStatus === COURSE_ORDER_STATUS_PENDING_REVIEW;

  function getItemStatus(item) {
    if (item.access_granted) {
      return <Badge bg="success">Access Granted</Badge>;
    }
    if (item.review_status) {
      return <Badge bg="warning" text="dark">{COURSE_REVIEW_STATUS_NAMES[item.review_status]}</Badge>;
    }
    return null;
  }

  return (
    <div>
      <PageHeader pageTitle={order ? `Order #${order.id}` : 'Order'}/>
      <Container fluid className="usacm-container-medium">

        {getErrorMessageForField(errors, '') &&
          <Row>
            <Col className="text-center mb-3 usacm-error-message">
              {getErrorMessageForField(errors, '')}
            </Col>
          </Row>
        }

        {order &&
          <>
            <div className="mb-4">
              {orderStatus === COURSE_ORDER_STATUS_APPROVED &&
                <div className="fs-5">Thank you! Your payment was received and you now have access.</div>
              }
              {orderStatus === COURSE_ORDER_STATUS_PENDING_REVIEW &&
                <div className="fs-5">
                  Thank you! Your order was received. Some of it must be approved before you get access - you will
                  get access to those once they are approved.
                </div>
              }
              {orderStatus === COURSE_ORDER_STATUS_PENDING &&
                <div className="fs-5">Your payment is being processed. You will get access once it has gone through.</div>
              }
              {orderStatus === COURSE_ORDER_STATUS_DECLINED &&
                <div className="fs-5 usacm-error-message">Your payment was declined.</div>
              }
              {paymentFailed &&
                <div className="fs-5 usacm-error-message">
                  Your payment did not go through. Your cart is still there to try again.
                </div>
              }
              {waitingForStripe &&
                <div className="fs-5">We are waiting for the payment to be confirmed. This usually takes a few seconds.</div>
              }
              {!paymentFailed && !waitingForStripe && !completed &&
                orderStatus !== COURSE_ORDER_STATUS_PENDING && orderStatus !== COURSE_ORDER_STATUS_DECLINED &&
                <div className="fs-5">This order is {COURSE_ORDER_STATUS_NAMES[orderStatus]}.</div>
              }
            </div>

            {completed && purchasedHtml && <div className="mb-4" dangerouslySetInnerHTML={{__html: purchasedHtml}}/>}

            {order.items.map(item =>
              <Card key={item.id} className="mb-3">
                <Card.Body className="d-flex flex-wrap align-items-center gap-3">
                  <div className="flex-grow-1">
                    <div className="fw-semibold">{item.item_text}</div>
                    {item.course_price &&
                      <div className="small text-muted">{getCoursePriceAccessStr(item.course_price)}</div>
                    }
                  </div>
                  {getItemStatus(item)}
                  <div className="fw-semibold course-catalog-amount">{getCoursePriceAmountStr(item.item_amount)}</div>
                </Card.Body>
              </Card>
            )}

            <div className="d-flex justify-content-end align-items-center gap-3 mt-4">
              <span className="fs-5">Total</span>
              <span className="fs-5 fw-semibold course-catalog-amount">{getCoursePriceAmountStr(order.amount)}</span>
            </div>

            <Row className="mt-4">
              <Col className="usacm-button-row">
                {waitingForStripe && <Button variant="secondary" onClick={() => loadOrder()}>Check Again</Button>}
                {paymentFailed && <Button onClick={() => navigate('/course-cart')}>Back to Cart</Button>}
                {completed &&
                  <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
                }
              </Col>
            </Row>
          </>
        }

      </Container>
    </div>
  );
}
